import {
  GeckoAssetModelType,
  GeckoComponentModelType,
} from 'types/assetModel/assetModelEnums'
import {
  assetModelFormFields,
  GeckoAssetModel,
} from 'types/entries/geckoAssetModel'
import {
  FormField,
  getFormFieldByDataKey,
} from 'types/geckoFormTypesAndHelpers'
import { RolodexEntryJson } from 'types/index'
import { parseDate, parseNumber } from 'utils/parsingHelpers'

import { BaseAssetModelData } from './baseAssetModelData'
import { HoodBendAssetModelJSON } from './components/hood/geckoHoodBend'
import { HoodStraightAssetModelJSON } from './components/hood/geckoHoodStraight'

export type GeckoHoodAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.Hood
  installation_date: string | undefined
  components: Record<
    string,
    HoodStraightAssetModelJSON | HoodBendAssetModelJSON
  >
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
}

export const HoodAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.HoodStraight,
  GeckoComponentModelType.HoodBend,
]

export class GeckoHoodAssetModel extends GeckoAssetModel<BaseAssetModelData> {
  private constructor(json: RolodexEntryJson) {
    super(json)
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoHoodAssetModel {
    return new GeckoHoodAssetModel(json)
  }

  public asJSON(): GeckoHoodAssetModelJSON {
    return generateHoodAssetModel(
      this.data.name,
      this.data.installation_date,
      this.raw.data.components as Record<
        string,
        HoodStraightAssetModelJSON | HoodBendAssetModelJSON
      >,
      this.data.effective_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation
    )
  }
}

export function generateHoodAssetModel(
  name: string,
  installation_date: Date | undefined,
  components: Record<
    string,
    HoodStraightAssetModelJSON | HoodBendAssetModelJSON
  >,
  effective_date: Date,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined
): GeckoHoodAssetModelJSON {
  const hoodInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  const hoodEffectiveDate = effective_date.toISOString().split('T')[0]
  return {
    name: name,
    type: GeckoAssetModelType.Hood,
    installation_date: hoodInstallationDate,
    components: components,
    effective_date: hoodEffectiveDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
  }
}

export const hoodModelFormFields = (
  assetModelJSON: GeckoHoodAssetModelJSON
) => {
  const assetModelfields = assetModelFormFields(assetModelJSON)
  const hoodModelFormFields: FormField[] = [
    // TODO: Add hood-specific form fields here
  ]
  return [...assetModelfields, ...hoodModelFormFields]
}

export const HoodJSONFromFormFields = (
  assetModelJSON: GeckoHoodAssetModelJSON,
  formFields: FormField[]
) => {
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generateHoodAssetModel(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    parseNumber(
      getFormFieldByDataKey(formFields, 'compass_orientation')?.value
    ),
    parseNumber(
      getFormFieldByDataKey(formFields, 'plant_north_compass_orientation')
        ?.value
    )
  )
}
